/**
 * Accessible data table styled with ICDS `--ic-*` design tokens.
 *
 * NOTE: ICDS `ic-data-table` is NOT published in the public `@ukic/react` v3
 * package (it lives in an internal `@ukic/canvas` package that is not on the
 * npm registry available to this build). This component is a faithful,
 * token-themed, keyboard-accessible stand-in with the same column/row API shape
 * so it can be swapped for `<IcDataTable>` once that package is available.
 *
 * It uses a native semantic <table> with proper roles, sortable-ready headers,
 * and a clickable-row affordance.
 */
import type { ReactNode } from "react";

export interface Column<Row> {
  /** Stable key. */
  key: string;
  /** Header label. */
  header: string;
  /** Cell renderer. */
  cell: (row: Row) => ReactNode;
  /** Optional column width (CSS length). */
  width?: string;
  /** Align cell content. */
  align?: "left" | "right" | "center";
}

interface DataTableProps<Row> {
  caption: string;
  columns: Column<Row>[];
  rows: Row[];
  rowKey: (row: Row) => string;
  /** If provided, rows become clickable (and keyboard-activatable). */
  onRowClick?: (row: Row) => void;
  emptyMessage?: string;
}

export function DataTable<Row>({
  caption,
  columns,
  rows,
  rowKey,
  onRowClick,
  emptyMessage = "No rows.",
}: DataTableProps<Row>) {
  return (
    <div className="hl-table-wrap" role="region" aria-label={caption} tabIndex={0}>
      <table className="hl-table">
        <caption className="hl-visually-hidden">{caption}</caption>
        <thead>
          <tr>
            {columns.map((c) => (
              <th
                key={c.key}
                scope="col"
                style={{ width: c.width, textAlign: c.align ?? "left" }}
              >
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td className="hl-table-empty" colSpan={columns.length}>
                {emptyMessage}
              </td>
            </tr>
          ) : (
            rows.map((row) => {
              const clickable = Boolean(onRowClick);
              return (
                <tr
                  key={rowKey(row)}
                  className={clickable ? "hl-row-clickable" : undefined}
                  onClick={clickable ? () => onRowClick!(row) : undefined}
                  tabIndex={clickable ? 0 : undefined}
                  role={clickable ? "button" : undefined}
                  onKeyDown={
                    clickable
                      ? (e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            onRowClick!(row);
                          }
                        }
                      : undefined
                  }
                >
                  {columns.map((c) => (
                    <td key={c.key} style={{ textAlign: c.align ?? "left" }}>
                      {c.cell(row)}
                    </td>
                  ))}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}
